import React from "react";
import { Link } from "react-router-dom";

// Provider-logo strip rendered on the dark editorial panel. Acts as social
// proof -- these are live research firms with portfolios on Plutus. Shared by the sign-in and connector approval pages.
//
// Image treatment: all provider assets are opaque (no transparent bg), so
// high-contrast monochrome filters plus lighten blending remove their source
// rectangles against the V7 Ink panel. Aurelion is the lone light-on-dark
// source asset, so it does not use the inversion applied to the others.
type ProviderLogo = { slug: string; src: string; alt: string; filter: "invert" | "grayscale" };
// Slug is the canonical analytics id for each provider surfaced in the
// strip. Keep it stable even if the display name / asset path changes,
// so AUTH_SOCIAL_PROOF_VIEW payloads stay comparable across releases.
export const PROVIDER_LOGOS: ProviderLogo[] = [
  { slug: "citrini", src: "/provider-logos/citrini.png", alt: "Citrini Research", filter: "invert" },
  { slug: "le-shrub", src: "/provider-logos/le-shrub.png", alt: "Le Shrub", filter: "invert" },
  { slug: "ap-research", src: "/provider-logos/ap-research.png", alt: "AP Research", filter: "invert" },
  { slug: "aurelion", src: "/provider-logos/aurelion.png", alt: "Aurelion Research", filter: "grayscale" },
  { slug: "hfi-research", src: "/provider-logos/hfi-research.png", alt: "HFI Research", filter: "invert" },
  { slug: "eliant", src: "/provider-logos/eliant.png", alt: "Eliant Capital", filter: "invert" },
];

const ProviderLogoStrip: React.FC = () => (
  <div
    className="hidden border-t border-v7-white/20 pt-5 lg:block"
    role="group"
    aria-label="Featured independent research firms"
  >
    <div className="grid grid-cols-6 gap-3">
      {PROVIDER_LOGOS.map(providerLogo => (
        <img
          key={providerLogo.src}
          src={providerLogo.src}
          alt={providerLogo.alt}
          loading="lazy"
          className={`aspect-[1.45] w-full object-contain opacity-60 mix-blend-lighten transition-opacity duration-200 hover:opacity-90 ${
            providerLogo.filter === "invert"
              ? "invert grayscale contrast-[400%]"
              : "grayscale contrast-[400%] brightness-[180%]"
          }`}
        />
      ))}
    </div>
  </div>
);

interface AuthBrandPanelProps {
  headline?: React.ReactNode;
}

export default function AuthBrandPanel({
  headline = <>An amazing portfolio <span className="font-v7-serif font-normal italic">awaits</span></>,
}: AuthBrandPanelProps) {
  return (
      <section
        className="relative isolate flex min-h-[360px] flex-col overflow-hidden bg-v7-ink px-5 py-6 text-v7-white sm:px-8 lg:min-h-screen lg:min-h-[100dvh] lg:px-12 lg:py-9"
        aria-label="Plutus"
      >
        <div
          className="pointer-events-none absolute -left-32 -top-40 h-96 w-96 rounded-full bg-v7-bronze/10 blur-3xl"
          aria-hidden="true"
        />
        <div
          className="pointer-events-none absolute -bottom-40 -right-40 h-96 w-96 rounded-full bg-v7-white/[0.04] blur-3xl"
          aria-hidden="true"
        />

        <Link
          to="/"
          className="relative z-10 inline-flex min-h-11 w-fit items-center gap-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v7-white"
          aria-label="Plutus home"
        >
          <img src="/favicon.png" alt="" className="h-9 w-9 object-contain brightness-0 invert" />
          <span className="text-xs font-medium tracking-[0.24em]">PLUTUS</span>
        </Link>

        <div className="relative z-10 my-auto py-12 lg:py-16">
          <p className="max-w-[10ch] text-[42px] font-light leading-[0.98] tracking-[-0.055em] sm:text-5xl lg:text-[56px] xl:text-[64px]">
            {headline}
          </p>
        </div>

        <ProviderLogoStrip />
      </section>
  );
}
